import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { useNotifications } from '/src/hooks';
import { isSemrush } from '/src/utils/semrush';
import { isDevelopment, isE2E } from '/src/utils';

const VERSION_POLL_MS = 60_000;
const DEBUG_SERVER_VERSION_KEY = 'ce.shell.debug.serverVersion';
const DEBUG_POLL_MS_KEY = 'ce.shell.debug.versionPollMs';
const PENDING_RELOAD_VERSION_KEY = 'ce.shell.pendingReloadVersion';
const LAST_RELOAD_KEY = 'shell:lastReload';
const LAST_AUTO_RELOAD_VERSION_KEY = 'shell:lastAutoReloadVersion';
const RELOAD_COOLDOWN_MS = 24 * 60 * 60 * 1000; // 24 hours
const QUERY_RELOAD_GUARD_MS = 5 * 60 * 1000; // 5 minutes
const WHITELISTED_PATHS_FOR_RELOAD = [
  '/home',
  '/live',
  '/goals',
  '/snapshots',
  '/recordings',
  '/errors',
  '/traffic',
  '/funnels',
  '/ab-tests',
  '/addons',
  '/web-analytics',
];

function canUseDebugVersion() {
  return isDevelopment() || isE2E();
}

function getDebugServerVersion(search) {
  if (!canUseDebugVersion()) return null;

  const fromSession = window.sessionStorage.getItem(DEBUG_SERVER_VERSION_KEY);
  if (fromSession) return fromSession;

  const fromQuery = new URLSearchParams(search).get('shellServerVersion');
  if (fromQuery) return fromQuery;

  return null;
}

function getPollIntervalMs() {
  const fromWindow = Number(window.CE_DEBUG_VERSION_POLL_MS);
  if (Number.isFinite(fromWindow) && fromWindow >= 100) return fromWindow;

  if (!canUseDebugVersion()) return VERSION_POLL_MS;

  const fromSession = window.sessionStorage.getItem(DEBUG_POLL_MS_KEY);
  if (!fromSession) return VERSION_POLL_MS;

  const parsed = Number(fromSession);
  if (!Number.isFinite(parsed) || parsed < 100) return VERSION_POLL_MS;

  return parsed;
}

function getPendingReloadVersion() {
  try {
    return window.sessionStorage.getItem(PENDING_RELOAD_VERSION_KEY);
  } catch {
    return null;
  }
}

function acknowledgeVersion(version) {
  try {
    window.sessionStorage.setItem(PENDING_RELOAD_VERSION_KEY, version);
  } catch {
    console.error('[VERSION] failed to persist pending reload version');
  }
}

function clearPendingReloadVersion() {
  try {
    window.sessionStorage.removeItem(PENDING_RELOAD_VERSION_KEY);
  } catch {
    console.error('[VERSION] failed to clear pending reload version');
  }
}

function hardReloadWithCacheBuster(href) {
  const reloadSource = isSemrush ? window.location.href : href;
  const nextUrl = new URL(reloadSource, window.location.origin);
  nextUrl.searchParams.set('ce_reload_ts', String(Date.now()));
  window.location.replace(nextUrl.toString());
}

function wasRecentlyReloadedFromQuery(search) {
  const reloadTs = Number(new URLSearchParams(search).get('ce_reload_ts'));
  if (!Number.isFinite(reloadTs)) return false;

  const elapsedMs = Date.now() - reloadTs;
  return elapsedMs >= 0 && elapsedMs < QUERY_RELOAD_GUARD_MS;
}

function getCurrentBundleVersion() {
  return window.version || window.SHELL_VERSION || null;
}

export function OutdatedBundleHandler() {
  const notifications = useNotifications();
  const navigate = useNavigate();
  const { pathname, search, hash } = useLocation();
  const href = `${pathname}${search}${hash}`;
  const lastPathname = useRef('');
  const [serverVersion, setServerVersion] = useState(window.version ?? null);

  useEffect(() => {
    if (lastPathname.current === pathname) return;

    lastPathname.current = pathname;

    if (!WHITELISTED_PATHS_FOR_RELOAD.includes(pathname)) return;
    const bundleVersion = getCurrentBundleVersion();
    if (!serverVersion || !bundleVersion || serverVersion === bundleVersion) return;
    if (wasRecentlyReloadedFromQuery(search)) return;

    try {
      const lastReloadRaw = window.localStorage.getItem(LAST_RELOAD_KEY);
      const lastAutoReloadVersion = window.localStorage.getItem(LAST_AUTO_RELOAD_VERSION_KEY);

      if (lastAutoReloadVersion === serverVersion) return;

      const lastReload = Number(lastReloadRaw);
      const canReload = !Number.isFinite(lastReload) || Date.now() - lastReload >= RELOAD_COOLDOWN_MS;

      if (canReload) {
        window.localStorage.setItem(LAST_AUTO_RELOAD_VERSION_KEY, serverVersion);
        window.localStorage.setItem(LAST_RELOAD_KEY, String(Date.now()));
        hardReloadWithCacheBuster(href);
      }
    } catch {
      console.error('[VERSION] failed to evaluate automatic reload state');
    }
  }, [pathname, search, href, serverVersion]);

  useEffect(() => {
    const bundleVersion = getCurrentBundleVersion();
    if (!serverVersion || !bundleVersion || serverVersion !== bundleVersion) return;

    try {
      const lastAutoReloadVersion = window.localStorage.getItem(LAST_AUTO_RELOAD_VERSION_KEY);
      if (lastAutoReloadVersion !== serverVersion) return;

      window.localStorage.removeItem(LAST_RELOAD_KEY);
    } catch {
      console.error('[VERSION] failed to clear stale automatic reload timestamp');
    }
  }, [serverVersion]);

  useEffect(() => {
    const params = new URLSearchParams(search);
    if (!params.has('ce_reload_ts')) return;

    params.delete('ce_reload_ts');

    navigate(
      {
        pathname,
        search: params.toString() ? `?${params.toString()}` : '',
        hash,
      },
      { replace: true },
    );

    if (!isSemrush) return;

    const iframeParams = new URLSearchParams(window.location.search);
    if (!iframeParams.has('ce_reload_ts')) return;

    iframeParams.delete('ce_reload_ts');

    window.history.replaceState(
      {},
      '',
      `${window.location.pathname}${iframeParams.toString() ? `?${iframeParams.toString()}` : ''}${window.location.hash}`,
    );
  }, [navigate, pathname, search, hash]);

  useEffect(() => {
    // when mounted initially, get the first version immediately, and store it in the window.
    const fetchVersion = async () => {
      try {
        const debugVersion = getDebugServerVersion(search);
        if (debugVersion) {
          setServerVersion(debugVersion);
          return { version: debugVersion };
        }

        const request = await fetch(`/v.json?ts=${Date.now()}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
          cache: 'no-store',
        });
        const data = await request.json();
        setServerVersion(data.version);

        if (!window.version) {
          window.version = data.version;
        }
        return data;
      } catch {
        console.error('[VERSION] fetch failed');
      }
    };

    const checkVersionAndNotify = (newData) => {
      const bundleVersion = getCurrentBundleVersion();
      const pendingReloadVersion = getPendingReloadVersion();

      if (newData?.version === bundleVersion && pendingReloadVersion) {
        clearPendingReloadVersion();
      }

      if (newData && newData.version !== bundleVersion && newData.version !== pendingReloadVersion) {
        const serverVersion = newData.version;

        // the version has changed, notify the user to reload the page to get the latest changes.
        notifications.info({
          id: 'new_app_version',
          title: 'New version available',
          onDismiss: () => {
            acknowledgeVersion(serverVersion);
          },
          content: (
            <span className="flex items-center">
              Please
              <button
                onClick={() => {
                  acknowledgeVersion(serverVersion);
                  hardReloadWithCacheBuster(href);
                }}
                className="text-link mx-1 !text-white !underline"
              >
                reload the page
              </button>
              to get the latest changes.
            </span>
          ),
        });
      }
    };

    // then set up the interval to check for new versions every minute

    const intervalId = window.setInterval(async () => {
      const newData = await fetchVersion();
      checkVersionAndNotify(newData);
    }, getPollIntervalMs());

    fetchVersion().then(checkVersionAndNotify);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [notifications, search, href]);

  return null;
}
